import { useCallback, useEffect, useRef, useState } from "react";
import { ep } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { Button, ErrorState, Skeleton } from "../../components/ui";
import { haptic } from "../../lib/motion";
import { CARD, DEFAULT_SHARE_OPTIONS, drawShareCard, type ShareOptions } from "./share-card";
import "./share.css";
import { useDialog } from "../../lib/use-dialog";
import { queryKeys } from "@oshimap/query";

const TOGGLES: Array<[keyof ShareOptions, string]> = [
  ["name", "이름"],
  ["places", "숫자"],
  ["cities", "지역"],
  ["stamps", "스탬프"],
];

/**
 * FR-PASS-12 / OD-014.
 * NFR-PV-05: 사용자가 의도하지 않은 정보가 카드에 들어가지 않는다 —
 * 무엇이 들어가는지 미리 보여주고, 항목마다 끌 수 있게 한다.
 */
export function ShareSheet({ onClose }: { onClose: () => void }) {
  const dialog = useDialog(onClose);

  const card = useQuery(queryKeys.shareCard(), (signal) => ep.getShareCard({ signal }));
  const [opts, setOpts] = useState<ShareOptions>(DEFAULT_SHARE_OPTIONS);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || card.state.status !== "success") return;
    const g = el.getContext("2d");
    if (!g) return;
    drawShareCard(g, card.state.data, opts);
  }, [card.state, opts]);

  const toBlob = useCallback(async (): Promise<Blob | null> => {
    const el = canvasRef.current;
    if (!el) return null;
    return new Promise((resolve) => el.toBlob((b) => resolve(b), "image/png"));
  }, []);

  const share = useCallback(async () => {
    setBusy(true); setNote(null);
    try {
      const blob = await toBlob();
      if (!blob) throw new Error("카드를 만들지 못했어요.");
      const file = new File([blob], "oshimap-passport.png", { type: "image/png" });

      /* 공유 시트가 있으면 그걸 쓴다 — 어디로 보낼지는 사용자가 고른다. */
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "덕질 여권" });
        haptic("STAMP");
        return;
      }
      /*
       * 없으면 내려받기. 우리가 어디로 보내지 않는다.
       *
       * 앵커를 문서에 붙였다가 지운다 — Firefox 는 문서에 없는 앵커의
       * `click()` 을 무시한다. 그리고 **바로 revoke 하지 않는다**: 내려받기는
       * click() 이 끝난 뒤에 blob 을 읽으므로, 그 자리에서 취소하면 브라우저에
       * 따라 빈 파일이 되거나 아무 일도 일어나지 않는다.
       */
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "oshimap-passport.png";
      a.style.display = "none";
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      haptic("STAMP");
      /*
       * "저장했어요" 라고 단언하지 않는다. 브라우저가 저장했는지, 사람이 취소
       * 했는지 우리는 알 수 없다 — 되지 않은 일을 됐다고 말하지 않는다.
       */
      setNote("이미지를 내려받습니다. 저장 위치는 브라우저 설정을 따릅니다.");
    } catch (e) {
      /* 사용자가 공유 시트를 닫은 것은 실패가 아니다. */
      if (e instanceof DOMException && e.name === "AbortError") return;
      setNote(e instanceof Error ? e.message : "공유하지 못했어요.");
    } finally { setBusy(false); }
  }, [toBlob]);

  return (
    <div ref={dialog.ref} className="om-visit" role="dialog" aria-modal="true" aria-label="공유 카드">
      <div className="om-visit__form om-share">
        <h2 className="om-visit__title">공유 카드</h2>
        <p className="om-visit__note">
          여기 보이는 것만 들어갑니다. 사진이나 남의 이미지는 넣지 않아요.
        </p>

        {card.state.status === "loading" || card.state.status === "idle" ? <Skeleton lines={4} />
        : card.state.status === "error" ? (
          <ErrorState level={3} title="카드를 불러오지 못했어요"
            body={card.state.error.message} onRetry={card.reload} />
        ) : (
          <>
            <canvas ref={canvasRef} width={CARD.w} height={CARD.h}
              className="om-share__canvas" aria-label="공유 카드 미리보기" role="img" />

            <div className="om-share__toggles" role="group" aria-label="카드에 넣을 항목">
              {TOGGLES.map(([k, label]) => (
                <button key={k} type="button" className="om-chip" aria-pressed={opts[k]}
                  onClick={() => setOpts((o) => ({ ...o, [k]: !o[k] }))}>
                  {label}
                </button>
              ))}
            </div>

            {note ? <p className="om-note" role="status">{note}</p> : null}

            <div className="om-visit__actions">
              <Button variant="ghost" onClick={onClose} disabled={busy}>닫기</Button>
              <Button variant="primary" style={{ flex: 1 }} disabled={busy}
                onClick={() => void share()}>
                {busy ? "만드는 중…" : "공유하기"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
